import * as d3 from 'd3';
import type { DotCell, PersonAtPlace } from '../domain/DotCell';
import type { LineagePath } from '../domain/LineagePath';
import { AppStore } from '../store/AppStore';
import { TimelineStore } from '../store/TimelineStore';
import { SelectionStore } from '../store/SelectionStore';
import { Component } from './Component';
import type { MapView } from './MapView';
import { branchDisplayName, branchLabel } from './BranchPanel';
import { createLogger } from '../utils/logger';

const log = createLogger('DotLayer');

/** Internal per-dot datum, computed from DotCell + stores. */
export interface DotDatum {
  key: string; // location key, also used as D3 data key
  lat: number;
  lng: number;
  weight: number; // cumulative event count up to currentDecade
  color: string; // dominant branch color, or '#999999' if unassigned
  brickWallCount: number;
  branchCounts: Map<string, number>; // for tooltip
  placeName: string; // human-readable place name for tooltip
  minDecade: number; // earliest visible decade at this location
  maxDecade: number; // latest visible decade at this location
  approximate: boolean; // any contributing location matched only a broader area
  unverified: boolean; // any contributing place names no state or country
  people: Map<string, PersonAtPlace[]>; // branchId → ancestors with events here, for tooltip
}

/** Names listed under each branch line in the tooltip before "+N more". */
const MAX_NAMES_SHOWN = 3;

const STAR_COLOR = '#F0C040'; // gold — brick-wall only (Phase 3 concentric rings are NOT gold)
const STAR_PATH = d3.symbol().type(d3.symbolStar).size(50)(); // computed once; same string every time
export const TOOLTIP_STYLE =
  'position:fixed;background:rgba(0,0,0,0.82);color:#fff;padding:6px 10px;' +
  'border-radius:4px;font-size:12px;line-height:1.5;pointer-events:none;' +
  'display:none;z-index:9000;max-width:260px;';

export class DotLayer extends Component {
  private dotGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private starGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private tooltip!: HTMLDivElement;

  constructor(private readonly mapView: MapView) {
    super();
  }

  render(): HTMLElement {
    // DotLayer renders into the MapView SVG group, not an HTML element.
    return document.createElement('span');
  }

  mount(_parent: HTMLElement): void {
    const layerGroup = d3.select(this.mapView.getLayerGroup());

    // Dot circles rendered below stars (z-order via DOM insertion order)
    this.dotGroup = layerGroup
      .append('g')
      .attr('class', 'dot-layer')
      .attr('data-testid', 'dot-layer')
      .attr('aria-hidden', 'true'); // decorative SVG — accessible data in BrickWallPanel

    // Stars appended after dots so they paint on top
    this.starGroup = layerGroup
      .append('g')
      .attr('class', 'star-layer')
      .attr('data-testid', 'star-layer')
      .attr('aria-hidden', 'true'); // decorative SVG

    // Tooltip div lives outside the SVG so it can overflow the map container
    this.tooltip = document.createElement('div');
    this.tooltip.setAttribute('data-testid', 'dot-tooltip');
    this.tooltip.style.cssText = TOOLTIP_STYLE;
    document.body.appendChild(this.tooltip);

    this.track(AppStore.subscribe(() => this._redraw()));
    this.track(TimelineStore.subscribe(() => this._redraw()));
    this.track(SelectionStore.subscribe(() => this._redraw()));
    this.track(this.mapView.onViewChanged(() => this._reproject()));

    this._redraw();
    log.info('DotLayer mounted');
  }

  unmount(): void {
    this.dotGroup?.remove();
    this.starGroup?.remove();
    this.tooltip?.remove();
    super.unmount();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  /** Reproject all dots and stars on map pan/zoom. No data join, no animation. */
  private _reproject(): void {
    this.dotGroup
      .selectAll<SVGCircleElement, DotDatum>('circle.dot')
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y);

    this.starGroup.selectAll<SVGPathElement, DotDatum>('path.star').attr('transform', (d) => {
      const pt = this.mapView.projectPoint(d.lat, d.lng);
      return `translate(${pt.x},${pt.y})`;
    });
  }

  /** Full data join. Called when AppStore, TimelineStore, or SelectionStore changes. */
  private _redraw(): void {
    const { dotMap, branchPaths } = AppStore.getState();
    const { currentDecade } = TimelineStore.getState();
    const { activeBranchId } = SelectionStore.getState();

    const dotOpacity = (d: DotDatum) =>
      activeBranchId === null || d.branchCounts.has(activeBranchId) ? 0.85 : 0.1;

    const dots = buildDotData(dotMap, branchPaths, currentDecade);
    const maxWeight = dots.reduce((m, d) => Math.max(m, d.weight), 1);
    const rScale = d3.scaleSqrt<number>().domain([1, maxWeight]).range([3, 24]);

    // ── Circles ──────────────────────────────────────────────────────────────
    const circles = this.dotGroup
      .selectAll<SVGCircleElement, DotDatum>('circle.dot')
      .data(dots, (d) => d.key);

    circles.exit().remove();

    circles
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y)
      .attr('r', (d) => rScale(d.weight))
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', dotOpacity);

    const entered = circles
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('pointer-events', 'auto') // override SVG pointer-events:none for hover
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y)
      .attr('r', 0)
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', dotOpacity)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1);

    entered
      .transition()
      .duration(300)
      .attr('r', (d) => rScale(d.weight));

    entered.merge(circles).on('mouseenter', (event: MouseEvent, d: DotDatum) => {
      this._showTooltip(event, d);
    });
    entered.merge(circles).on('mouseleave', () => {
      this._hideTooltip();
    });

    // ── Stars (brick-wall overlay) ────────────────────────────────────────────
    const brickWallDots = dots.filter((d) => d.brickWallCount > 0);

    const stars = this.starGroup
      .selectAll<SVGPathElement, DotDatum>('path.star')
      .data(brickWallDots, (d) => d.key);

    stars.exit().remove();

    stars
      .attr('transform', (d) => {
        const pt = this.mapView.projectPoint(d.lat, d.lng);
        return `translate(${pt.x},${pt.y})`;
      })
      .attr('fill-opacity', dotOpacity);

    stars
      .enter()
      .append('path')
      .attr('class', 'star')
      .attr('d', STAR_PATH)
      .attr('fill', STAR_COLOR)
      .attr('fill-opacity', dotOpacity)
      .attr('pointer-events', 'none')
      .attr('transform', (d) => {
        const pt = this.mapView.projectPoint(d.lat, d.lng);
        return `translate(${pt.x},${pt.y})`;
      });

    log.debug(
      `DotLayer: ${dots.length} dots, ${brickWallDots.length} stars at decade ${currentDecade}`,
    );
  }

  private _showTooltip(event: MouseEvent, d: DotDatum): void {
    this.tooltip.innerHTML = '';

    // Place name — user-controlled GEDCOM data, set via textContent
    const heading = document.createElement('strong');
    heading.textContent = d.placeName || `${d.lat.toFixed(2)}°, ${d.lng.toFixed(2)}°`;
    this.tooltip.appendChild(heading);

    // Branch names come from GEDCOM person names — textContent only
    const { branchPaths, graph } = AppStore.getState();
    const branchNames = new Map(branchPaths.map((p) => [p.branchId, branchDisplayName(p, graph)]));
    for (const text of tooltipLines(d, branchNames)) {
      const line = document.createElement('div');
      line.textContent = text;
      this.tooltip.appendChild(line);
    }

    this.tooltip.style.display = 'block';
    this.tooltip.style.left = `${event.clientX + 14}px`;
    this.tooltip.style.top = `${event.clientY - 14}px`;
  }

  private _hideTooltip(): void {
    this.tooltip.style.display = 'none';
  }
}

// ── Module-level pure helpers (exported for unit tests) ──────────────────────

/**
 * Tooltip lines below the place-name heading: decade range, one line per branch
 * ("{great-grandparent name} — {count} event(s)") followed by the ancestors behind
 * that count, approximate- and unverified-location notes, and
 * brick-wall summary. `branchNames` maps branchId → display name; unknown ids fall
 * back to the path label. Exported for unit tests.
 */
export function tooltipLines(d: DotDatum, branchNames: Map<string, string>): string[] {
  const lines = [
    d.minDecade === d.maxDecade ? `${d.minDecade}s` : `${d.minDecade}s – ${d.maxDecade}s`,
  ];

  for (const [branchId, count] of d.branchCounts.entries()) {
    if (count > 0) {
      lines.push(`${branchNames.get(branchId) ?? branchLabel(branchId)} — ${count} event(s)`);
      const names = peopleLine(d.people.get(branchId) ?? []);
      if (names !== '') {
        lines.push(names);
      }
    }
  }

  if (d.approximate) {
    lines.push('Approximate location');
  }

  if (d.unverified) {
    lines.push('Unverified location — place name has no state or country');
  }

  if (d.brickWallCount > 0) {
    lines.push(`★ ${d.brickWallCount} brick-wall ancestor${d.brickWallCount > 1 ? 's' : ''}`);
  }

  return lines;
}

/**
 * "Ann Lee (1692), Bob Lee, Cy Lee (1701) +2 more" — at most MAX_NAMES_SHOWN names,
 * in first-seen order; empty for no people. Exported for unit tests.
 */
export function peopleLine(people: PersonAtPlace[]): string {
  const shown = people
    .slice(0, MAX_NAMES_SHOWN)
    .map((p) => (p.year === undefined ? p.name : `${p.name} (${p.year.toString()})`));
  const hidden = people.length - shown.length;
  return shown.join(', ') + (hidden > 0 ? ` +${hidden.toString()} more` : '');
}

/**
 * Parse a location key back to lat/lng.
 * Key format: `${Math.round(lat*4)/4},${Math.round(lng*4)/4}`
 */
export function parseLocationKey(key: string): { lat: number; lng: number } {
  const parts = key.split(',');
  return { lat: Number(parts[0]), lng: Number(parts[1]) };
}

/**
 * Compute cumulative event weight for a DotCell up to and including `decade`.
 * Exported for unit tests.
 */
export function cumulativeWeight(cell: DotCell, decade: number): number {
  let total = 0;
  for (const [d, count] of cell.weightByDecade) {
    if (d <= decade) {
      total += count;
    }
  }
  return total;
}

/**
 * Select the dominant branch color for a dot (highest branchCount).
 * Returns '#999999' if branchCounts is empty. Exported for unit tests.
 */
export function dominantBranchColor(
  branchCounts: Map<string, number>,
  colorMap: Map<string, string>,
): string {
  let bestBranch: string | undefined;
  let bestCount = 0;
  for (const [branchId, count] of branchCounts) {
    if (count > bestCount) {
      bestCount = count;
      bestBranch = branchId;
    }
  }
  return bestBranch !== undefined ? (colorMap.get(bestBranch) ?? '#999999') : '#999999';
}

/**
 * Build the DotDatum array from store state.
 * Only includes dots with cumulative weight > 0 at `currentDecade`.
 * Pure function — no DOM or map access. Exported for unit tests.
 */
export function buildDotData(
  dotMap: Map<string, DotCell>,
  branchPaths: LineagePath[],
  currentDecade: number,
): DotDatum[] {
  const colorMap = new Map(branchPaths.map((p) => [p.branchId, p.branchColor]));
  const result: DotDatum[] = [];

  for (const [key, cell] of dotMap) {
    const weight = cumulativeWeight(cell, currentDecade);
    if (weight === 0) {
      continue;
    }
    // Drawn at the cell's anchor (average of its places); the grid point is a fallback
    const { lat, lng } = cell.anchor ?? parseLocationKey(key);
    const color = dominantBranchColor(cell.branchCounts, colorMap);

    const visibleDecades = [...cell.weightByDecade.keys()].filter((d) => d <= currentDecade);
    const minDecade = Math.min(...visibleDecades);
    const maxDecade = Math.max(...visibleDecades);

    result.push({
      key,
      lat,
      lng,
      weight,
      color,
      brickWallCount: cell.brickWallCount,
      branchCounts: cell.branchCounts,
      placeName: cell.placeName,
      minDecade,
      maxDecade,
      approximate: cell.approximate === true,
      unverified: cell.unverified === true,
      people: cell.people ?? new Map(),
    });
  }
  return result;
}
